/**
 * Joins one `subscriptionMirrors` row to the entitlement records that cite it
 * (#1705).
 *
 * The admin user card shows two sections that read two different stores. They
 * legitimately disagree, and labelling them "Billing" and "Access" says they
 * are different without saying how a given row on one side relates to the
 * other. Without that an admin has to eyeball subscription ids across two
 * lists.
 *
 * ## The join key
 *
 * Entitlement storage keys its `subscriptions` map by
 * `` `${provider}:${providerSubscriptionId ?? 'legacy'}` `` — the same pair the
 * mirror stores. The join is on the PAIR of raw fields, deliberately not on the
 * composite string: the mirror's document id sanitizes `/` and `:` to `_`, so
 * the two composite strings are not guaranteed to be equal even when they name
 * the same subscription.
 *
 * ## Why "no match" is the answer this module is most careful about
 *
 * `GET /v1/users/:uid/entitlements` returns only entitlements that are ACTIVE
 * from some source. Within one of those, `subscriptions[]` carries every grant,
 * active and inactive. A match settles the question. An ABSENCE settles far
 * less than it appears to, and there are three separate reasons a real,
 * access-granting subscription can fail to match:
 *
 * 1. **The entitlement is gone entirely.** Inactive from every source ⇒ absent
 *    from the payload, along with any record it holds.
 * 2. **The grant predates subscription-id stamping.** V1 storage never
 *    persisted the id, so records from the backfill key as `stripe:legacy` and
 *    reach the wire with the field omitted. `externalSubscriptionService`
 *    treats `undefined` and `'legacy'` as "could be this subscription" when
 *    revoking, and so must this.
 * 3. **External-provider storage keeps ONE record per provider.**
 *    `applyOrRevokeEntitlement` pins it to a *representative* subscription and
 *    drops the rest, while the mirror holds one row per subscription. A user
 *    with two active supapass subscriptions therefore has a row that no record
 *    can ever cite — a permanent, guaranteed non-match on a live subscription.
 *
 * So `not-cited` carries whether another active grant for the same provider
 * exists elsewhere, and (2) resolves to `ambiguous`. Neither may produce a
 * divergence flag.
 */

import type { UserEntitlements } from '../types/entitlement';
import {
  SUBSCRIPTION_PROVIDERS,
  type AdminSubscriptionInfo,
} from '../types/subscriptionMirror';

/**
 * The sentinel a pre-migration grant carries instead of a real subscription id.
 * `externalSubscriptionService` matches on it, alongside `undefined`, when
 * revoking legacy grants.
 */
const LEGACY_SUBSCRIPTION_ID = 'legacy';

export type AccessLink =
  /** At least one active entitlement holds a live grant for this subscription. */
  | { state: 'granting'; entitlementIds: string[] }
  /** Cited by an active entitlement, but that grant is not itself live. */
  | { state: 'recorded-inactive'; entitlementIds: string[] }
  /**
   * An active grant for this provider carries no usable subscription id, so it
   * might be this subscription and might not. Nothing may be concluded.
   */
  | { state: 'ambiguous'; entitlementIds: string[] }
  /**
   * No ACTIVE entitlement cites it. `providerHasOtherActiveGrant` says whether
   * the user nonetheless holds live access from this provider through a
   * different subscription — in which case this row's own contribution is
   * unknowable, because external storage keeps one record per provider.
   */
  | { state: 'not-cited'; providerHasOtherActiveGrant: boolean }
  /** Entitlements have not loaded, so nothing can be said either way. */
  | { state: 'unknown' };

export function resolveAccessLink(
  row: AdminSubscriptionInfo,
  entitlements: UserEntitlements | undefined
): AccessLink {
  if (!entitlements) return { state: 'unknown' };

  const granting = new Set<string>();
  const inactive = new Set<string>();
  const ambiguous = new Set<string>();
  let providerHasOtherActiveGrant = false;

  for (const [entitlementId, detail] of Object.entries(entitlements)) {
    for (const grant of detail.subscriptions ?? []) {
      if (grant.provider !== row.provider) continue;

      if (
        !grant.providerSubscriptionId ||
        grant.providerSubscriptionId === LEGACY_SUBSCRIPTION_ID
      ) {
        if (grant.isActive) ambiguous.add(entitlementId);
        continue;
      }

      if (grant.providerSubscriptionId !== row.providerSubscriptionId) {
        if (grant.isActive) providerHasOtherActiveGrant = true;
        continue;
      }

      // Deliberately does not stop at the first match. A duplicate, however it
      // arose, must not let a dead record hide a live one and report access as
      // absent while the user has it.
      if (grant.isActive) granting.add(entitlementId);
      else inactive.add(entitlementId);
    }
  }

  // A live grant anywhere wins: the user HAS access through this subscription,
  // and reporting otherwise would be the one wrong answer that matters.
  if (granting.size > 0) {
    return { state: 'granting', entitlementIds: [...granting].sort() };
  }
  // Before the negative answers: an unidentifiable active grant is exactly the
  // case where claiming "nothing grants on this" would be wrong.
  if (ambiguous.size > 0) {
    return { state: 'ambiguous', entitlementIds: [...ambiguous].sort() };
  }
  if (inactive.size > 0) {
    return { state: 'recorded-inactive', entitlementIds: [...inactive].sort() };
  }
  return { state: 'not-cited', providerHasOtherActiveGrant };
}

/**
 * Whether the two stores disagree about this subscription in a way an admin
 * should look at, and in which direction.
 *
 * ## How much of a disagreement is ordinary lag
 *
 * Less than it looks, and it differs by provider — which is why these are
 * worded as observations rather than verdicts.
 *
 * For **Stripe** the entitlement work and `upsertStripeSubscription` run in ONE
 * handler in one request, entitlements first. A mirror row existing therefore
 * implies the entitlement write already committed, and there is no queue
 * between them for a row to sit in. A disagreement is usually a real gap: the
 * entitlement path skipped the subscription (an uncatalogued product grants
 * nothing — #1715), or the reconcile healed the mirror, which by design never
 * writes entitlements.
 *
 * For **supapass** the projection is deliberately EARLIER than the entitlement
 * work — `externalSubscriptionService` projects immediately after writing the
 * source document, precisely so a grant that throws cannot leave the mirror
 * stale. So a supapass mirror row genuinely can exist before, or without, the
 * entitlement write that should accompany it.
 *
 * There is client-side lag in both cases too: the two panels are two reads, one
 * of them cached.
 *
 * ## What is deliberately never flagged
 *
 * - `on_hold` — retries are exhausted and the subscription is suspended, so
 *   granting nothing is the expected outcome rather than a gap.
 * - `pending` — not yet effective, so granting nothing is correct.
 * - `paused` — the Stripe path leaves the entitlement unchanged, so either
 *   answer is defensible.
 * - `unknown` and `ambiguous` — nothing is established.
 * - `not-cited` where the user holds another live grant from the same provider.
 *   External storage keeps one record per provider, so a second live
 *   subscription is UNCITEABLE by construction and its non-match means nothing.
 *
 * `grace` IS flagged in the not-granting direction, deliberately: `past_due`
 * retains access on purpose, so access being absent while the provider still
 * considers the subscription live is the literal "why did this user lose
 * access?" question. It is not flagged in the granting direction, where
 * retained access is the intended behaviour. This also matches
 * `isActionableGap`, which already counts `grace` as actionable on the same row
 * — without it one row could show a red catalog gap and no divergence flag at
 * once.
 */
export type BillingAccessDivergence =
  /** Provider says live (or in grace); no active entitlement grants on it. */
  | 'live-not-granting'
  /** Provider says it is over; an active entitlement still grants on it. */
  | 'ended-still-granting'
  | null;

/** States where access is expected to be RETAINED, so its absence is a gap. */
const ACCESS_EXPECTED: ReadonlySet<string> = new Set(['active', 'grace']);

/** States where the subscription is over, so continued access is a gap. */
const ACCESS_EXPECTED_GONE: ReadonlySet<string> = new Set([
  'ended',
  'suppressed',
]);

export function billingAccessDivergence(
  row: AdminSubscriptionInfo,
  link: AccessLink
): BillingAccessDivergence {
  if (link.state === 'unknown' || link.state === 'ambiguous') return null;

  if (ACCESS_EXPECTED.has(row.normalizedStatus) && link.state !== 'granting') {
    if (link.state === 'not-cited' && link.providerHasOtherActiveGrant) {
      return null;
    }
    return 'live-not-granting';
  }
  if (
    ACCESS_EXPECTED_GONE.has(row.normalizedStatus) &&
    link.state === 'granting'
  ) {
    return 'ended-still-granting';
  }
  return null;
}

/**
 * Whether a mirror row describes a subscription that is over.
 *
 * Deliberately its own constant rather than a reuse of `ACCESS_EXPECTED_GONE`
 * above. That set is a DIVERGENCE predicate — "continued access here would be a
 * gap" — and it includes `suppressed`, which must not be filed as history:
 * `applyWebhookEvent` pins a suppressed doc at `ECHO_SUPPRESSED`, so a
 * suppressed subscription that later genuinely ends looks identical on disk to
 * one still live (`functions/src/scripts/undo-echo-suppression.ts`). Calling it
 * "past" would be a statement the data does not support.
 *
 * The other non-current states are not history either: `on_hold`, `paused` and
 * `pending` are all live subscriptions in an unusual condition, and an admin
 * asking "what is this user paying for now" needs to see them.
 */
const PAST_STATES: ReadonlySet<string> = new Set(['ended']);

/** Whether this row belongs under "past subscriptions" rather than current. */
export function isPastSubscription(row: AdminSubscriptionInfo): boolean {
  return PAST_STATES.has(row.normalizedStatus);
}

/**
 * A subscription-shaped grant held in the ENTITLEMENT record that no mirror row
 * accounts for.
 *
 * The mirror is not the only record of a past subscription. A grant survives in
 * `subscriptions[]` after it stops granting, and some such grants have no
 * mirror row at all — pre-migration ones, and any whose row was never
 * backfilled. Without this the card reports "no subscription is mirrored
 * against this uid" while the data it already holds knows about one.
 */
export interface UnmirroredGrant {
  provider: string;
  /** `null` for a pre-migration grant, which never persisted the id. */
  providerSubscriptionId: string | null;
  productIdentifier: string | null;
  isActive: boolean;
  expiresAt: string | null;
  purchaseDate: string | null;
  /** Every active entitlement whose record carries this grant. */
  entitlementIds: string[];
}

/**
 * The providers `subscriptionMirrors` actually covers.
 *
 * Derived from the API's own provider enum rather than hardcoded, so widening
 * the mirror widens this with it. **This whitelist is load-bearing, not
 * defensive.** `subscriptions[]` is not "stripe and supapass": redeeming a code
 * pushes `{ provider: 'redeem', isActive: true, providerSubscriptionId: <the
 * redemption code> }` (`functions/src/services/redeemCodeService.ts`), and a
 * redeem grant can never have a mirror row. Without the filter, every code a
 * user ever redeemed would be reported here as an unmirrored subscription —
 * printing the code itself — on the panel that says what a PROVIDER thinks
 * about BILLING.
 */
const MIRRORED_PROVIDERS: ReadonlySet<string> = new Set(SUBSCRIPTION_PROVIDERS);

/**
 * Grants the entitlement record holds that no loaded mirror row accounts for.
 *
 * Returns `[]` rather than guessing whenever absence is not knowable:
 *
 * - `entitlements` still loading — the same trap `UserEditModal` documents,
 *   where an in-flight read renders as a positive finding of nothing.
 * - `complete === false` — the caller has only the first page of mirror rows,
 *   so a grant may be matched by a row on page two.
 *
 * A grant with no id is only reported when the user has NO rows for that
 * provider at all, because it cannot be told apart from any of them. Where they
 * do have rows, an ACTIVE id-less grant is still reported per row by
 * `resolveAccessLink` as `ambiguous`; an INACTIVE one is reported by neither,
 * and is visible only as a greyed chip on the Access panel. That is a real
 * cost, accepted deliberately: the alternative is printing a card that may be
 * a duplicate of a row already on screen, on a panel whose whole value is that
 * its statements are true.
 */
export function resolveUnmirroredGrants(
  rows: AdminSubscriptionInfo[],
  entitlements: UserEntitlements | undefined,
  options: { complete: boolean }
): UnmirroredGrant[] {
  if (!entitlements || !options.complete) return [];

  const mirroredIds = new Set<string>();
  const providersWithRows = new Set<string>();
  for (const row of rows) {
    providersWithRows.add(row.provider);
    mirroredIds.add(`${row.provider}:${row.providerSubscriptionId}`);
  }

  // Keyed so the SAME grant written under several entitlements collapses to one
  // entry. It is written once per entitlement in the bundle — see the
  // `for (const entitlementId of entitlementIds)` loop in `subscriptionService`
  // — so without this a two-entitlement bundle lists every subscription twice.
  const found = new Map<string, UnmirroredGrant>();

  for (const [entitlementId, detail] of Object.entries(entitlements)) {
    for (const grant of detail.subscriptions ?? []) {
      if (!MIRRORED_PROVIDERS.has(grant.provider)) continue;

      const rawId = grant.providerSubscriptionId;
      const hasId = Boolean(rawId) && rawId !== LEGACY_SUBSCRIPTION_ID;

      if (hasId) {
        if (mirroredIds.has(`${grant.provider}:${rawId}`)) continue;
      } else if (providersWithRows.has(grant.provider)) {
        continue;
      }

      const key = `${grant.provider}:${hasId ? rawId : LEGACY_SUBSCRIPTION_ID}`;
      const existing = found.get(key);
      if (existing) {
        if (!existing.entitlementIds.includes(entitlementId)) {
          existing.entitlementIds.push(entitlementId);
        }
        // A grant recorded live under any entitlement is live — but its DATES
        // must travel with it. OR-ing the flag alone produced "Grant: active"
        // beside an expiry in the past, because the id-less key can merge two
        // records that are not the same subscription.
        if (grant.isActive && !existing.isActive) {
          existing.isActive = true;
          existing.expiresAt = grant.expiresAt;
          existing.purchaseDate = grant.purchaseDate ?? null;
          existing.productIdentifier = grant.productIdentifier ?? null;
        }
        continue;
      }
      found.set(key, {
        provider: grant.provider,
        providerSubscriptionId: hasId ? (rawId as string) : null,
        productIdentifier: grant.productIdentifier ?? null,
        isActive: grant.isActive,
        expiresAt: grant.expiresAt,
        purchaseDate: grant.purchaseDate ?? null,
        entitlementIds: [entitlementId],
      });
    }
  }

  return [...found.values()]
    .map(g => ({ ...g, entitlementIds: [...g.entitlementIds].sort() }))
    .sort((a, b) => a.provider.localeCompare(b.provider));
}
